import { computed, ref, watch } from "vue";
import * as Sentry from "@sentry/vue";
import { useToast } from "vue-toastification";
import type { ApolloError } from "@apollo/client/core";
import type { ToastID } from "vue-toastification/dist/types/types";

import { useGetProjectSpeckleCopySubscription } from "@/graphql";

import SpeckleCopySpinnerIcon from "../components/SpeckleCopySpinnerIcon.vue";
import { refreshCopiedProject } from "./refreshCopiedProject";
import {
  copiedMessage,
  copyFailedMessage,
  copyingMessage,
  speckleCopyTransitions,
  type SpeckleCopyRecord,
} from "./speckleCopyToasts.helpers";

/**
 * Says "Copying Speckle Project…" while a cloned project's Speckle geometry is being copied,
 * replaces it when the copy lands, and re-reads the project so the 3D viewer appears without a
 * reload (RTLCA-5478).
 *
 * Cloning a project with "create new project" selected gives the copy an empty Speckle project;
 * the geometry follows from a background job, because the copy takes 78-131s for a real project.
 * Without this the viewer is simply blank for a minute or two with nothing to explain it.
 *
 * Mounted once, app-wide, rather than on the project page: the clone modal navigates away the
 * instant it returns, and the copy outlives whatever page the user goes to next. The subscription
 * is unfiltered — Hasura's row permission already scopes it to the user's own projects.
 *
 * Failures get a toast too. A copy that quietly does not happen leaves an empty viewer that looks
 * exactly like a successful one, which is the state this whole feature exists to end.
 */
export function useSpeckleCopy() {
  const toast = useToast();
  const openToasts = new Map<string, ToastID>();
  const previous = ref<SpeckleCopyRecord[]>([]);

  const { result, onError } = useGetProjectSpeckleCopySubscription();

  onError((error: ApolloError) => {
    // An environment whose Hasura predates the table answers "not found in type:
    // 'subscription_root'". Nothing to do about it here and nothing worth showing the user — the
    // toast is a courtesy, and its absence is how the app behaved before this shipped.
    Sentry.captureException(error);
  });

  const records = computed<SpeckleCopyRecord[]>(() => (result.value?.projectSpeckleCopyRecent ?? []) as SpeckleCopyRecord[]);

  const dismiss = (projectId: string) => {
    const open = openToasts.get(projectId);
    if (open === undefined) return;
    toast.dismiss(open);
    openToasts.delete(projectId);
  };

  watch(records, (current) => {
    for (const transition of speckleCopyTransitions(previous.value, current)) {
      const { projectId, projectName } = transition;

      if (transition.kind === "started") {
        dismiss(projectId);
        openToasts.set(
          projectId,
          toast.info(copyingMessage(projectName), {
            // No timeout: the copy runs for minutes, and a toast that vanished after five seconds
            // would leave the blank viewer unexplained for the rest of it.
            timeout: false,
            // A spinner rather than the info (i): this one sits open while work happens, and a
            // still icon on a toast that never closes reads as a notice nobody dismissed.
            icon: SpeckleCopySpinnerIcon,
            // The app-wide default is `alert`, which is assertive. Work in progress is a status;
            // the success and failure toasts below keep the assertive default, where it belongs.
            accessibility: { toastRole: "status", closeButtonLabel: "Close notification" },
          })
        );
        continue;
      }

      dismiss(projectId);

      if (transition.kind === "succeeded") {
        toast.success(copiedMessage(projectName));
        // Not awaited: the toast must not wait on two REST calls, and the refresh is best-effort.
        void refreshCopiedProject(projectId);
      } else {
        toast.error(copyFailedMessage(projectName));
      }
    }

    previous.value = [...current];
  });
}
